import React, { useState } from "react";
import {
  Heart,
  Users,
  Calendar as CalendarIcon,
  AlertTriangle,
  TrendingUp,
  Clock,
  ChevronLeft,
  ChevronRight,
  Bell,
  MessageSquare,
  ClipboardCheck,
  Users as TeamIcon,
  User,
  ChevronDown,
} from "lucide-react";
import { Button } from "@/components/controls/customControls/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/controls/customControls/card";
import { useDashboardData } from "./useDashboardData";
import DashboardSkeleton, {
  AppointmentsSkeleton,
  TeamFilterSkeleton,
  StatsSkeleton,
  NotificationsSkeleton,
  ActiveConsultationsSkeleton,
} from "./DashboardSkeleton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  getScheduleEventsByUser,
  getActiveConsultationsByUser,
} from "../../dispatchers/pharmacyDashboardDispatchers";
import {
  setShowPermissionModal,
  setShowPermissionMessage,
} from "@/dispatchers/appDispatchers";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import {
  accessTitanverseConsultationUpsert,
  accessTitanverseCalenderUpsert,
} from "@/data/constantStrings";

export default function Dashboard() {
  const {
    selectedDate,
    goToPreviousDay,
    goToNextDay,
    goToToday,
    isToday,
    formatSelectedDate,
    selectedDateAppointments,
    thisWeekAppointments,
    isDarkMode,
    onAppointmentClick,
    isPharmacyDashboardLoading,
    isPharmacyScheduleLoading,
    isActiveConsultationsLoading,
    isPharmacyNotificationsLoading,
    kpiStats,
    // New pagination variables
    paginatedConsultations,
    totalConsultations,
    consultationsPage,
    setConsultationsPage,
    totalPages,
    startIndex,
    endIndex,
    // Notifications pagination
    paginatedNotifications,
    totalNotifications,
    notificationsPage,
    setNotificationsPage,
    totalNotificationsPages,
    notificationsStartIndex,
    notificationsEndIndex,
    currentTeamMembers,
    currentUser,
    currentPharmacyId,
    handleConsultationClick,
  } = useDashboardData();

  const pharmacists = React.useMemo(() => {
    const list = (currentTeamMembers || []).map((member: any) => ({
      fullName: member.userFullName,
      emailAddress: member.email,
    }));

    if (
      currentUser?.emailAddress &&
      !list.some((p) => p.emailAddress === currentUser.emailAddress)
    ) {
      list.unshift({
        fullName: currentUser.fullName,
        emailAddress: currentUser.emailAddress,
      });
    }
    return list;
  }, [currentTeamMembers, currentUser]);
  const [selectedPharmacists, setSelectedPharmacists] = useState<string[]>(
    currentUser?.emailAddress ? [currentUser.emailAddress] : [],
  );
  // Temporary state for the dropdown selection
  const [tempSelectedPharmacists, setTempSelectedPharmacists] = useState<
    string[]
  >(currentUser?.emailAddress ? [currentUser.emailAddress] : []);

  const [isTeamFilterOpen, setIsTeamFilterOpen] = useState(false);

  const isAllSelected = selectedPharmacists.length === pharmacists.length;

  const fetchDashboardData = async (emails: string[]) => {
    if (currentPharmacyId) {
      getScheduleEventsByUser(currentPharmacyId, emails);
      getActiveConsultationsByUser(currentPharmacyId, emails);
    }
  };

  const selectOnlyMe = () => {
    if (currentUser?.emailAddress) {
      const emails = [currentUser.emailAddress];
      setSelectedPharmacists(emails);
      setTempSelectedPharmacists(emails);
      fetchDashboardData(emails);
    }
  };

  const selectAllPharmacists = () => {
    const allEmails = pharmacists.map((p) => p.emailAddress);
    setTempSelectedPharmacists(allEmails);
  };

  const clearAllPharmacists = () => {
    const newSelection = currentUser?.emailAddress
      ? [currentUser.emailAddress]
      : [];
    setTempSelectedPharmacists(newSelection);
    setSelectedPharmacists(newSelection);
    fetchDashboardData(newSelection);
  };

  const togglePharmacist = (email: string) => {
    setTempSelectedPharmacists((prev) =>
      prev.includes(email) ? prev.filter((p) => p !== email) : [...prev, email],
    );
  };

  const handleApplyFilters = () => {
    setSelectedPharmacists(tempSelectedPharmacists);
    fetchDashboardData(tempSelectedPharmacists);
    setIsTeamFilterOpen(false);
  };

  const handleCancelFilters = () => {
    setTempSelectedPharmacists(selectedPharmacists);
    setIsTeamFilterOpen(false);
  };

  const getNotificationIcon = (
    type: "alert" | "pre-consult",
    severity?: "critical" | "warning" | "info",
  ) => {
    if (type === "pre-consult") {
      return <ClipboardCheck className="w-4 h-4 text-green-500" />;
    }
    switch (severity) {
      case "critical":
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case "warning":
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case "info":
        return <AlertTriangle className="w-4 h-4 text-blue-500" />;
      default:
        return <AlertTriangle className="w-4 h-4 text-gray-500" />;
    }
  };
  const hasConsultationPermission = checkUserPermissions(
    accessTitanverseConsultationUpsert,
  );

  const hasUpsertPermission = checkUserPermissions(
    accessTitanverseCalenderUpsert,
  );

  // Appointment status badge logic
  const getStatusBadge = (status: string) => {
    const normalizedStatus = (status || "").toLowerCase().trim();

    let badgeContent = "Unknown";
    let colorClass = "border-gray-400 text-gray-600 bg-gray-30";

    switch (normalizedStatus) {
      case "completed":
      case "confirmed":
        badgeContent = "Confirmed";
        colorClass = "border-green-600 text-green-600 bg-green-30";
        break;
      case "active":
      case "scheduled":
        badgeContent = "Active";
        colorClass = "border-blue-600 text-blue-600 bg-blue-30";
        break;
      case "pending":
        badgeContent = "Pending";
        colorClass = "border-yellow-600 text-yellow-600 bg-yellow-10";
        break;
      default:
        badgeContent = normalizedStatus || "No Status";
        colorClass = "border-gray-400 text-gray-600 bg-gray-10";
    }

    return (
      <span
        className={`inline-block px-2 py-1 rounded-full text-xs font-semibold border ${colorClass}`}
      >
        {badgeContent}
      </span>
    );
  };

  // Consultation status badge logic
  const getConsultationStatusBadge = (status: string) => {
    const normalizedStatus = (status || "").toLowerCase().trim();

    let badgeContent = "Unknown";
    let colorClass = "border-gray-400 text-gray-600 bg-gray-30";

    switch (normalizedStatus) {
      case "completed":
        badgeContent = "Completed";
        colorClass = "border-green-600 text-green-600 bg-green-30";
        break;
      case "active":
        badgeContent = "Active";
        colorClass = "border-blue-600 text-blue-600 bg-blue-10";
        break;
      case "paused":
        badgeContent = "Paused";
        colorClass = "border-orange-600 text-orange-600 bg-orange-30";
        break;
      case "pending":
        badgeContent = "Pending";
        colorClass = "border-yellow-600 text-yellow-600 bg-yellow-10";
        break;
      case "cancel":
        badgeContent = "Cancelled";
        colorClass = "border-red-600 text-red-600 bg-red-10";
        break;
      default:
        badgeContent = normalizedStatus || "No Status";
        colorClass = "border-gray-600 text-gray-600 bg-gray-10";
    }

    return (
      <span
        className={`inline-block px-2 py-1 rounded-full text-xs font-semibold border ${colorClass}`}
      >
        {badgeContent}
      </span>
    );
  };

  const renderPagination = (
    currentPage: number,
    totalPages: number,
    onPageChange: (page: number) => void,
  ) => {
    const pages: (number | string)[] = [];

    if (totalPages <= 4) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 2) {
        pages.push(1);
        pages.push(2);
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 1) {
        pages.push(1);
        pages.push("...");
        pages.push(totalPages - 1);
        pages.push(totalPages);
      } else {
        pages.push(1);
        pages.push("...");
        pages.push(currentPage);
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages.map((page, index) => (
      <button
        key={index}
        onClick={() => typeof page === "number" && onPageChange(page)}
        className={`px-2 py-1 text-xs rounded transition-colors duration-300 ${
          page === currentPage
            ? isDarkMode
              ? "bg-blue-600 text-white"
              : "bg-blue-500 text-white"
            : typeof page === "number"
              ? isDarkMode
                ? "text-gray-300 hover:bg-gray-700"
                : "text-gray-600 hover:bg-gray-100"
              : "text-gray-500 cursor-default"
        }`}
      >
        {page}
      </button>
    ));
  };

  return (
    <div
      className={`h-screen flex flex-col transition-colors duration-300 ${
        isDarkMode ? "bg-gray-900" : "bg-gray-50"
      }`}
    >
      <div
        className={`flex-1 p-4 overflow-auto transition-colors duration-300 ${
          isDarkMode ? "bg-gray-900" : "bg-gray-50"
        }`}
      >
        <div className="flex flex-col w-full h-full">
          {/* Dashboard Content - Two Column Layout */}
          <div className="flex flex-1 gap-4 overflow-hidden">
            {/* Left Sidebar - 1/3 width */}
            <div className="flex flex-col w-1/3 space-y-4 overflow-y-auto scrollbar-hover-thin">
              {/* Today's Appointments Panel */}
              {isPharmacyScheduleLoading || isPharmacyDashboardLoading ? (
                <AppointmentsSkeleton isDarkMode={isDarkMode} />
              ) : (
                <Card
                  className={`flex-1 flex flex-col transition-colors duration-300 ${
                    isDarkMode
                      ? "bg-gray-800 border-gray-700"
                      : "bg-white border-gray-200"
                  }`}
                >
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4" />
                        <div>
                          <CardTitle
                            className={`text-sm font-semibold transition-colors duration-300 ${
                              isDarkMode ? "text-white" : "text-black"
                            }`}
                          >
                            Today's Appointments
                          </CardTitle>
                          <CardDescription className="text-xs">
                            {selectedDateAppointments?.length}{" "}
                            {selectedDateAppointments?.length === 1
                              ? "appointment"
                              : "appointments"}{" "}
                            scheduled
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {!isToday(selectedDate) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={goToToday}
                            className={`h-8 text-xs transition-colors duration-300 ${
                              isDarkMode
                                ? "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                                : "bg-white border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            Today
                          </Button>
                        )}
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={goToPreviousDay}
                            disabled={isToday(selectedDate)}
                            className={`h-8 w-8 transition-colors duration-300 ${
                              isDarkMode
                                ? "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                                : "bg-white border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            <ChevronLeft className="w-3.5 h-3.5" />
                          </Button>

                          <Button
                            variant="outline"
                            size="icon"
                            onClick={goToNextDay}
                            className={`h-8 w-8 transition-colors duration-300 ${
                              isDarkMode
                                ? "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                                : "bg-white border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            <ChevronRight className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    <div
                      className={`text-sm font-medium mt-2 transition-colors duration-300 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {formatSelectedDate(selectedDate)}
                    </div>
                  </CardHeader>
                  <CardContent className="flex flex-col flex-1 pt-3 overflow-hidden">
                    {selectedDateAppointments?.length === 0 ? (
                      <div
                        className={`text-center py-8 transition-colors duration-300 ${
                          isDarkMode
                            ? "bg-gray-700 border-gray-600"
                            : "bg-gray-50 border-gray-200"
                        } rounded-lg border`}
                      >
                        <CalendarIcon
                          className={`w-8 h-8 mx-auto mb-2 transition-colors duration-300 ${
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }`}
                        />
                        <p
                          className={`text-sm font-semibold transition-colors duration-300 ${
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }`}
                        >
                          No appointments scheduled
                        </p>
                        <p
                          className={`text-xs mt-1 transition-colors duration-300 ${
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }`}
                        >
                          There are no appointments for this date
                        </p>
                      </div>
                    ) : (
                      <div className="flex-1 overflow-y-auto scrollbar-hover-thin">
                        <div
                          className={`border rounded-lg overflow-hidden transition-colors duration-300 ${
                            isDarkMode
                              ? "bg-gray-800 border-gray-600"
                              : "bg-white border-gray-200"
                          }`}
                        >
                       {/* Time-based Grid */}
                          {(() => {
                            // Function to convert time string to 24-hour hour value
                            const getHour24 = (timeStr) => {
                              if (!timeStr) return 0;
                              const [time, modifier] = timeStr.split(' ');
                              let [hours] = time.split(':');
                              let hour = parseInt(hours);
                              
                              if (modifier === 'pm' && hour !== 12) {
                                hour += 12;
                              }
                              if (modifier === 'am' && hour === 12) {
                                hour = 0;
                              }
                              return hour;
                            };

                            // Get unique hours in 24-hour format and sort ASCENDING (earliest to latest)
                            const hours24 = Array.from(
                              new Set(
                                selectedDateAppointments?.map((apt) => {
                                  return getHour24(apt.time);
                                }),
                              ),
                            ).sort((a, b) => a - b);

                            // Group appointments by their 24-hour hour value
                            const appointmentsByHour = selectedDateAppointments.reduce(
                              (acc, apt) => {
                                const hour24 = getHour24(apt.time);
                                if (!acc[hour24]) acc[hour24] = [];
                                acc[hour24].push(apt);
                                return acc;
                              },
                              {} as Record<number, typeof selectedDateAppointments>,
                            );

                            return hours24?.map((hour24) => {
                              const hourAppointments = appointmentsByHour[hour24] || [];
                              
                              // Sort appointments within each hour by time (ascending)
                              const sortedHourAppointments = [...hourAppointments].sort((a, b) => {
                                const aMinutes = a.date.getHours() * 60 + a.date.getMinutes();
                                const bMinutes = b.date.getHours() * 60 + b.date.getMinutes();
                                return aMinutes - bMinutes;
                              });
                              
                              // Convert 24-hour hour to display format (12-hour without AM/PM)
                              const displayHour = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
                              
                              return (
                                <div
                                  key={hour24}
                                  className={`flex border-b last:border-b-0 transition-colors duration-300 ${
                                    isDarkMode
                                      ? "border-gray-600"
                                      : "border-gray-200"
                                  }`}
                                >
                                  {/* Time Label - Simplified without AM/PM */}
                                  <div
                                    className={`w-16 p-3 border-r flex-shrink-0 transition-colors duration-300 ${
                                      isDarkMode
                                        ? "bg-gray-700 border-gray-600"
                                        : "bg-gray-50 border-gray-200"
                                    }`}
                                  >
                                    <div
                                      className={`text-sm font-medium transition-colors duration-300 ${
                                        isDarkMode
                                          ? "text-gray-300"
                                          : "text-gray-600"
                                      }`}
                                    >
                                      {displayHour}:00
                                    </div>
                                  </div>

                                  {/* Appointments for this hour */}
                                  <div className="flex-1 p-3 min-h-[80px]">
                                    <div className="space-y-2">
                                      {sortedHourAppointments?.map((appointment) => (
                                        <div
                                          key={appointment.id}
                                          onClick={() => {
                                            if (!hasUpsertPermission) {
                                              setShowPermissionMessage(
                                                "Create or update calendar events",
                                              );
                                              setShowPermissionModal(true);
                                              return;
                                            }
                                            onAppointmentClick(
                                              appointment.date,
                                            );
                                          }}
                                          className={`p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                                            isDarkMode
                                              ? "bg-blue-900/40 hover:bg-blue-900/60"
                                              : "bg-blue-50 hover:bg-blue-100"
                                          }`}
                                        >
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-start flex-1">
                                              <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-1">
                                                  <span
                                                    className={`font-medium transition-colors duration-300 ${
                                                      isDarkMode
                                                        ? "text-blue-200"
                                                        : "text-blue-900"
                                                    }`}
                                                  >
                                                    {appointment.time} -{" "}
                                                    {appointment.service}
                                                  </span>
                                                  {getStatusBadge(
                                                    appointment.status,
                                                  )}
                                                </div>
                                                <div
                                                  className={`text-sm transition-colors duration-300 ${
                                                    isDarkMode
                                                      ? "text-blue-300"
                                                      : "text-blue-700"
                                                  }`}
                                                >
                                                  Patient:{" "}
                                                  {appointment.patientName}
                                                </div>
                                                {appointment.pharmacist && (
                                                  <div
                                                    className={`text-sm transition-colors duration-300 ${
                                                      isDarkMode
                                                        ? "text-blue-300"
                                                        : "text-blue-700"
                                                    }`}
                                                  >
                                                    Pharmacist:{" "}
                                                    {appointment.pharmacist}
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <div
                                                className={`text-sm transition-colors duration-300 ${
                                                  isDarkMode
                                                    ? "text-blue-300"
                                                    : "text-blue-700"
                                                }`}
                                              >
                                                {appointment.duration}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              );
                            });
                          })()}
                        
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right Main Content - 2/3 width */}
            <div className="w-2/3 overflow-y-auto scrollbar-hover-thin">
              <div className="space-y-4">
                {/* Team Member Filter */}
                {isPharmacyDashboardLoading ? (
                  <TeamFilterSkeleton isDarkMode={isDarkMode} />
                ) : (
                  <Card
                    className={`transition-colors duration-300 ${
                      isDarkMode
                        ? "bg-gray-800 border-gray-700"
                        : "bg-white border-gray-200"
                    }`}
                  >
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <TeamIcon
                            className={`w-5 h-5 transition-colors duration-300 ${
                              isDarkMode ? "text-blue-400" : "text-blue-500"
                            }`}
                          />
                          <div>
                            <Label
                              className={`font-medium text-sm transition-colors duration-300 ${
                                isDarkMode ? "text-white" : "text-gray-900"
                              }`}
                            >
                              Team Member Filter
                            </Label>
                            <p
                              className={`text-xs transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              {selectedPharmacists.length === 1 &&
                              selectedPharmacists[0] ===
                                currentUser?.emailAddress
                                ? `Showing only your data`
                                : selectedPharmacists.length ===
                                    pharmacists.length
                                  ? "Showing all team members"
                                  : `Showing ${selectedPharmacists.length} of ${pharmacists.length} team members`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={selectOnlyMe}
                            className={`h-8 text-xs transition-colors duration-300 ${
                              selectedPharmacists.length === 1 &&
                              selectedPharmacists[0] ===
                                currentUser?.emailAddress
                                ? isDarkMode
                                  ? "bg-blue-900 border-blue-600 text-blue-200"
                                  : "bg-blue-50 border-blue-500 text-blue-700"
                                : isDarkMode
                                  ? "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                                  : "bg-white border-gray-300 hover:bg-gray-50"
                            }`}
                          >
                            <User className="w-3 h-3 mr-1" />
                            Only Me
                          </Button>
                          <Popover
                            open={isTeamFilterOpen}
                            onOpenChange={(isOpen) => {
                              setIsTeamFilterOpen(isOpen);
                              if (isOpen) {
                                // Sync temp state with actual state when opening
                                setTempSelectedPharmacists(selectedPharmacists);
                              }
                            }}
                          >
                            <PopoverTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className={`h-8 text-xs transition-colors duration-300 ${
                                  isDarkMode
                                    ? "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                                    : "bg-white border-gray-300 hover:bg-gray-50"
                                }`}
                              >
                                <TeamIcon className="w-3 h-3 mr-1" />
                                Select Team ({selectedPharmacists.length})
                                <ChevronDown className="w-3 h-3 ml-1" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent
                              align="end"
                              className={`w-72 transition-colors duration-300 ${
                                isDarkMode
                                  ? "bg-gray-800 border-gray-700"
                                  : "bg-white border-gray-200"
                              }`}
                            >
                              <div className="space-y-3">
                                <div className="flex items-center justify-between pb-2 border-b">
                                  <h4
                                    className={`font-semibold text-sm transition-colors duration-300 ${
                                      isDarkMode
                                        ? "text-white"
                                        : "text-gray-900"
                                    }`}
                                  >
                                    Select Team Members
                                  </h4>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={
                                      tempSelectedPharmacists.length ===
                                      pharmacists.length
                                        ? clearAllPharmacists
                                        : selectAllPharmacists
                                    }
                                    className="text-xs h-7"
                                  >
                                    {tempSelectedPharmacists.length ===
                                    pharmacists.length
                                      ? "Clear All"
                                      : "Select All"}
                                  </Button>
                                </div>
                                <div className="space-y-2 overflow-y-auto max-h-64">
                                  {pharmacists.map((pharmacist) => (
                                    <div
                                      key={pharmacist.emailAddress}
                                      className={`flex items-center space-x-2 p-2 rounded-md cursor-pointer transition-colors duration-200 ${
                                        isDarkMode
                                          ? "hover:bg-gray-700"
                                          : "hover:bg-gray-50"
                                      }`}
                                      onClick={() =>
                                        togglePharmacist(
                                          pharmacist.emailAddress,
                                        )
                                      }
                                    >
                                      <Checkbox
                                        id={pharmacist.emailAddress}
                                        checked={tempSelectedPharmacists.includes(
                                          pharmacist.emailAddress,
                                        )}
                                        onCheckedChange={() =>
                                          togglePharmacist(
                                            pharmacist.emailAddress,
                                          )
                                        }
                                      />
                                      <label
                                        htmlFor={pharmacist.emailAddress}
                                        className={`flex-1 text-sm cursor-pointer transition-colors duration-300 ${
                                          isDarkMode
                                            ? "text-gray-200"
                                            : "text-gray-700"
                                        } ${
                                          pharmacist.emailAddress ===
                                          currentUser?.emailAddress
                                            ? "font-semibold"
                                            : ""
                                        }`}
                                      >
                                        {pharmacist.fullName}
                                        {pharmacist.emailAddress ===
                                          currentUser?.emailAddress && (
                                          <span
                                            className={`ml-2 text-xs transition-colors duration-300 ${
                                              isDarkMode
                                                ? "text-blue-400"
                                                : "text-blue-600"
                                            }`}
                                          >
                                            (You)
                                          </span>
                                        )}
                                      </label>
                                    </div>
                                  ))}
                                </div>
                                <div className="flex items-center gap-2 pt-3 mt-2 border-t">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCancelFilters}
                                    className={`flex-1 h-9 text-sm font-medium transition-colors duration-300 ${
                                      isDarkMode
                                        ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                                        : "border-gray-200 text-gray-700 hover:bg-gray-50"
                                    }`}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={handleApplyFilters}
                                    className={`flex-1 h-9 text-sm font-medium transition-colors duration-300 ${
                                      isDarkMode
                                        ? "bg-white text-black hover:bg-gray-200"
                                        : "bg-black text-white hover:bg-gray-900"
                                    }`}
                                  >
                                    Apply Filters
                                  </Button>
                                </div>
                              </div>
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Stats Overview */}
                {isPharmacyDashboardLoading ? (
                  <StatsSkeleton isDarkMode={isDarkMode} />
                ) : (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    {/* Total Active Patients */}
                    <Card
                      className={`transition-colors duration-300 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700"
                          : "bg-white border-gray-200"
                      }`}
                    >
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p
                              className={`text-xs font-medium transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              Total Active Patients
                            </p>
                            <h3
                              className={`text-2xl font-bold mt-2 transition-colors duration-300 ${
                                isDarkMode ? "text-white" : "text-black"
                              }`}
                            >
                              {kpiStats.activePatients.value}
                            </h3>
                            <div className="flex items-center gap-1 mt-2">
                              <TrendingUp className="w-3 h-3 text-green-500" />
                              <span className="text-xs font-medium text-green-500">
                                {kpiStats.activePatients.percentage}% from last
                                month
                              </span>
                            </div>
                          </div>
                          <div
                            className={`p-3 rounded-full transition-colors duration-300 ${
                              isDarkMode ? "bg-blue-900/40" : "bg-blue-50"
                            }`}
                          >
                            <Users
                              className={`w-6 h-6 transition-colors duration-300 ${
                                isDarkMode ? "text-blue-400" : "text-blue-600"
                              }`}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Total Active Services */}
                    <Card
                      className={`transition-colors duration-300 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700"
                          : "bg-white border-gray-200"
                      }`}
                    >
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p
                              className={`text-xs font-medium transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              Total Active Services
                            </p>
                            <h3
                              className={`text-2xl font-bold mt-2 transition-colors duration-300 ${
                                isDarkMode ? "text-white" : "text-black"
                              }`}
                            >
                              {kpiStats.activeServices.value}
                            </h3>
                            <div className="flex items-center gap-1 mt-2">
                              <TrendingUp className="w-3 h-3 text-green-500" />
                              <span className="text-xs font-medium text-green-500">
                                {kpiStats.activeServices.percentage}% new
                                services
                              </span>
                            </div>
                          </div>
                          <div
                            className={`p-3 rounded-full transition-colors duration-300 ${
                              isDarkMode ? "bg-purple-900/40" : "bg-purple-50"
                            }`}
                          >
                            <Heart
                              className={`w-6 h-6 transition-colors duration-300 ${
                                isDarkMode
                                  ? "text-purple-400"
                                  : "text-purple-600"
                              }`}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Total Appointments Today */}
                    <Card
                      className={`transition-colors duration-300 ${
                        isDarkMode
                          ? "bg-gray-800 border-gray-700"
                          : "bg-white border-gray-200"
                      }`}
                    >
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p
                              className={`text-xs font-medium transition-colors duration-300 ${
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }`}
                            >
                              Total Appointments Today
                            </p>
                            <h3
                              className={`text-2xl font-bold mt-2 transition-colors duration-300 ${
                                isDarkMode ? "text-white" : "text-black"
                              }`}
                            >
                              {isToday(selectedDate)
                                ? selectedDateAppointments.length
                                : thisWeekAppointments.filter((apt) =>
                                    isToday(apt.date),
                                  ).length}
                            </h3>
                            <div className="flex items-center gap-1 mt-2">
                              <Clock className="w-3 h-3 text-blue-500" />
                              <span className="text-xs font-medium text-blue-500">
                                {
                                  thisWeekAppointments.filter(
                                    (apt) =>
                                      isToday(apt.date) &&
                                      apt.status === "completed",
                                  ).length
                                }{" "}
                                confirmed
                              </span>
                            </div>
                          </div>
                          <div
                            className={`p-3 rounded-full transition-colors duration-300 ${
                              isDarkMode ? "bg-green-900/40" : "bg-green-50"
                            }`}
                          >
                            <CalendarIcon
                              className={`w-6 h-6 transition-colors duration-300 ${
                                isDarkMode ? "text-green-400" : "text-green-600"
                              }`}
                            />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                <div className="flex flex-col gap-4 lg:flex-row">
                  <div className="flex flex-col flex-1 space-y-4">
                    {/* Notifications */}
                    {isPharmacyNotificationsLoading ||
                    isPharmacyDashboardLoading ? (
                      <NotificationsSkeleton isDarkMode={isDarkMode} />
                    ) : (
                      <Card
                        className={`flex-1 flex flex-col transition-colors duration-300 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700"
                            : "bg-white border-gray-200"
                        }`}
                      >
                        <CardHeader className="pb-3">
                          <CardTitle
                            className={`flex items-center gap-2 text-sm font-semibold transition-colors duration-300 ${
                              isDarkMode ? "text-white" : "text-black"
                            }`}
                          >
                            <Bell className="w-4 h-4" />
                            Notifications
                          </CardTitle>
                          <CardDescription className="text-xs">
                            {totalNotifications} notifications
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="flex flex-col flex-1 pt-3 overflow-hidden">
                          <div className="space-y-2.5 flex-1 overflow-y-auto scrollbar-hover-thin">
                            {paginatedNotifications.map((notification) => (
                              <div
                                key={notification.id}
                                className={`flex items-start gap-2.5 p-3 rounded-lg border transition-colors duration-300 ${
                                  notification.type === "pre-consult"
                                    ? isDarkMode
                                      ? "bg-green-900/20 border-green-700"
                                      : "bg-green-50 border-green-200"
                                    : isDarkMode
                                      ? "bg-gray-700 border-gray-600"
                                      : "bg-gray-50 border-gray-200"
                                }`}
                              >
                                <div className="mt-0.5">
                                  {getNotificationIcon(
                                    notification.type,
                                    notification.severity,
                                  )}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4
                                    className={`font-semibold text-xs transition-colors duration-300 ${
                                      isDarkMode ? "text-white" : "text-black"
                                    }`}
                                  >
                                    {notification.title}
                                  </h4>
                                  <p
                                    className={`text-xs transition-colors duration-300 ${
                                      isDarkMode
                                        ? "text-gray-400"
                                        : "text-gray-600"
                                    }`}
                                  >
                                    {notification.message}
                                  </p>
                                  {notification.patientName && (
                                    <p
                                      className={`text-xs mt-1 transition-colors duration-300 ${
                                        isDarkMode
                                          ? "text-green-400"
                                          : "text-green-600"
                                      }`}
                                    >
                                      {new Date(
                                        notification.date,
                                      ).toLocaleDateString("en-GB")}
                                    </p>
                                  )}
                                </div>
                                <span
                                  className={`text-xs whitespace-nowrap transition-colors duration-300 ${
                                    isDarkMode
                                      ? "text-gray-500"
                                      : "text-gray-500"
                                  }`}
                                >
                                  {notification.time}
                                </span>
                              </div>
                            ))}
                          </div>
                          {totalNotificationsPages > 1 && (
                            <div
                              className={`mt-4 pt-3 border-t flex items-center justify-between transition-colors duration-300 ${
                                isDarkMode
                                  ? "border-gray-600"
                                  : "border-gray-200"
                              }`}
                            >
                              <div
                                className={`text-xs transition-colors duration-300 ${
                                  isDarkMode ? "text-gray-400" : "text-gray-600"
                                }`}
                              >
                                Showing {notificationsStartIndex + 1}-
                                {Math.min(
                                  notificationsEndIndex,
                                  totalNotifications,
                                )}{" "}
                                of {totalNotifications}
                              </div>

                              <div className="flex items-center gap-1">
                                {/* Previous Button */}
                                <button
                                  onClick={() =>
                                    setNotificationsPage((prev) =>
                                      Math.max(1, prev - 1),
                                    )
                                  }
                                  disabled={notificationsPage === 1}
                                  className={`p-1 rounded transition-colors duration-300 ${
                                    notificationsPage === 1
                                      ? isDarkMode
                                        ? "text-gray-600 cursor-not-allowed"
                                        : "text-gray-400 cursor-not-allowed"
                                      : isDarkMode
                                        ? "text-gray-300 hover:bg-gray-700"
                                        : "text-gray-600 hover:bg-gray-100"
                                  }`}
                                >
                                  <ChevronLeft className="w-4 h-4" />
                                </button>

                                {/* Page Numbers */}
                                {renderPagination(
                                  notificationsPage,
                                  totalNotificationsPages,
                                  setNotificationsPage,
                                )}

                                {/* Next Button */}
                                <button
                                  onClick={() =>
                                    setNotificationsPage((prev) =>
                                      Math.min(
                                        totalNotificationsPages,
                                        prev + 1,
                                      ),
                                    )
                                  }
                                  disabled={
                                    notificationsPage ===
                                    totalNotificationsPages
                                  }
                                  className={`p-1 rounded transition-colors duration-300 ${
                                    notificationsPage ===
                                    totalNotificationsPages
                                      ? isDarkMode
                                        ? "text-gray-600 cursor-not-allowed"
                                        : "text-gray-400 cursor-not-allowed"
                                      : isDarkMode
                                        ? "text-gray-300 hover:bg-gray-700"
                                        : "text-gray-600 hover:bg-gray-100"
                                  }`}
                                >
                                  <ChevronRight className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </div>

                  {/* Right Column - My Active Consultations (matching left column height) */}
                  <div className="flex flex-1">
                    {isActiveConsultationsLoading ||
                    isPharmacyDashboardLoading ? (
                      <ActiveConsultationsSkeleton isDarkMode={isDarkMode} />
                    ) : (
                      <Card
                        className={`flex-1 flex flex-col transition-colors duration-300 ${
                          isDarkMode
                            ? "bg-gray-800 border-gray-700"
                            : "bg-white border-gray-200"
                        }`}
                      >
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle
                                className={`flex items-center gap-2 text-sm font-semibold transition-colors duration-300 ${
                                  isDarkMode ? "text-white" : "text-black"
                                }`}
                              >
                                <MessageSquare className="w-4 h-4" />
                                My Active Consultations
                              </CardTitle>
                              <CardDescription className="text-xs">
                                {totalConsultations} active consultations
                              </CardDescription>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="flex flex-col flex-1 pt-3 overflow-hidden">
                          <div className="space-y-1.5 flex-1 overflow-y-auto scrollbar-hover-thin">
                            {paginatedConsultations?.map((consultation) => (
                              <div
                                key={consultation.id}
                                onClick={(e) => {
                                  if (!hasConsultationPermission) {
                                    e.preventDefault();
                                    setShowPermissionModal(true);
                                    setShowPermissionMessage(
                                      "You don't have permission to start consultation.",
                                    );
                                    return;
                                  }

                                  handleConsultationClick(consultation);
                                }}
                                className={`p-2 rounded-lg border transition-all duration-200 hover:shadow-md cursor-pointer ${
                                  isDarkMode
                                    ? "bg-gray-700 border-gray-600 hover:bg-gray-650"
                                    : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                                }`}
                              >
                                {/* Compact header: Name, Date/Time, and Status on one line */}
                                <div className="flex items-center justify-between gap-2 mb-1">
                                  <div className="flex items-center flex-1 min-w-0 gap-2">
                                    <h4
                                      className={`text-xs font-bold truncate transition-colors duration-300 ${
                                        isDarkMode ? "text-white" : "text-black"
                                      }`}
                                    >
                                      {consultation.patientName}
                                    </h4>
                                    <span
                                      className={`text-xs whitespace-nowrap transition-colors duration-300 ${
                                        isDarkMode
                                          ? "text-gray-500"
                                          : "text-gray-500"
                                      }`}
                                    >
                                      •
                                    </span>
                                    <span
                                      className={`text-xs whitespace-nowrap transition-colors duration-300 ${
                                        isDarkMode
                                          ? "text-gray-400"
                                          : "text-gray-600"
                                      }`}
                                    >
                                      {consultation.date} {consultation.time}
                                    </span>
                                  </div>
                                  {getConsultationStatusBadge(
                                    consultation.status,
                                  )}
                                </div>

                                {/* Consultation type */}
                                <p
                                  className={`text-xs transition-colors duration-300 ${
                                    isDarkMode
                                      ? "text-gray-400"
                                      : "text-gray-600"
                                  }`}
                                >
                                  {consultation.consultationType}
                                </p>
                              </div>
                            ))}
                          </div>
                          {totalPages > 1 && (
                            <div
                              className={`mt-4 pt-3 border-t flex items-center justify-between transition-colors duration-300 ${
                                isDarkMode
                                  ? "border-gray-600"
                                  : "border-gray-200"
                              }`}
                            >
                              <div
                                className={`text-xs transition-colors duration-300 ${
                                  isDarkMode ? "text-gray-400" : "text-gray-600"
                                }`}
                              >
                                Showing {startIndex + 1}-
                                {Math.min(endIndex, totalConsultations)} of{" "}
                                {totalConsultations}
                              </div>

                              <div className="flex items-center gap-1">
                                {/* Previous Button */}
                                <button
                                  onClick={() =>
                                    setConsultationsPage((prev) =>
                                      Math.max(1, prev - 1),
                                    )
                                  }
                                  disabled={consultationsPage === 1}
                                  className={`p-1 rounded transition-colors duration-300 ${
                                    consultationsPage === 1
                                      ? isDarkMode
                                        ? "text-gray-600 cursor-not-allowed"
                                        : "text-gray-400 cursor-not-allowed"
                                      : isDarkMode
                                        ? "text-gray-300 hover:bg-gray-700"
                                        : "text-gray-600 hover:bg-gray-100"
                                  }`}
                                >
                                  <ChevronLeft className="w-4 h-4" />
                                </button>

                                {/* Page Numbers */}
                                {renderPagination(
                                  consultationsPage,
                                  totalPages,
                                  setConsultationsPage,
                                )}

                                {/* Next Button */}
                                <button
                                  onClick={() =>
                                    setConsultationsPage((prev) =>
                                      Math.min(totalPages, prev + 1),
                                    )
                                  }
                                  disabled={consultationsPage === totalPages}
                                  className={`p-1 rounded transition-colors duration-300 ${
                                    consultationsPage === totalPages
                                      ? isDarkMode
                                        ? "text-gray-600 cursor-not-allowed"
                                        : "text-gray-400 cursor-not-allowed"
                                      : isDarkMode
                                        ? "text-gray-300 hover:bg-gray-700"
                                        : "text-gray-600 hover:bg-gray-100"
                                  }`}
                                >
                                  <ChevronRight className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
